import { useState } from 'react';
import { HiCalendar, HiViewGrid, HiViewList } from 'react-icons/hi';

export type ViewMode = 'grid' | 'table' | 'timeline';

const VIEW_MODE_STORAGE_KEY = 'campaign_planner_view_mode';

const isViewMode = (value: string | null): value is ViewMode =>
    value === 'grid' || value === 'table' || value === 'timeline';

export const useViewMode = (
    defaultMode: ViewMode = 'grid',
): [ViewMode, (mode: ViewMode) => void] => {
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        return isViewMode(saved) ? saved : defaultMode;
    });

    const updateViewMode = (mode: ViewMode) => {
        setViewMode(mode);
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    };

    return [viewMode, updateViewMode];
};

interface ViewModeSwitchProps {
    value: ViewMode;
    onChange: (mode: ViewMode) => void;
}

const VIEW_OPTIONS: { mode: ViewMode; icon: typeof HiViewGrid; title: string }[] = [
    { mode: 'grid', icon: HiViewGrid, title: 'Grid view' },
    { mode: 'table', icon: HiViewList, title: 'Table view' },
    { mode: 'timeline', icon: HiCalendar, title: 'Timeline view' },
];

export const ViewModeSwitch = ({ value, onChange }: ViewModeSwitchProps) => {
    return (
        <div className="flex glass rounded-lg p-1">
            {VIEW_OPTIONS.map(({ mode, icon: Icon, title }) => {
                const active = value === mode;
                return (
                    <button
                        key={mode}
                        className={`p-1.5 rounded-md transition-all ${
                            active
                                ? 'bg-accent shadow-sm text-gray-600'
                                : 'text-gray-500 hover:text-gray-700 cursor-pointer'
                        }`}
                        onClick={() => onChange(mode)}
                        title={title}
                    >
                        <Icon className="w-5 h-5" />
                    </button>
                );
            })}
        </div>
    );
};
